import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { TransferCard, TransferProps } from './TransferCard';
import { ItineraryDay, DayActivity } from './ItineraryDay';
import { findDestination } from '../data/destinations';
import { MapIcon, CalendarDaysIcon, ArrowRightIcon, PrinterIcon, HashIcon, SparklesIcon } from 'lucide-react';
interface RecommendationsPanelProps {
  destination: string;
  resort: string;
  departureDate: string;
  returnDate: string;
  onReset: () => void;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
// Rich pool of luxury activities
const morningPool = [{
  title: 'Sunrise Yoga on the Terrace',
  description: 'Begin your day with a private yoga session overlooking the coastline, guided by a certified wellness instructor.',
  duration: '1 Hour',
  category: 'Wellness'
}, {
  title: 'Private Beach Meditation',
  description: 'A guided mindfulness experience on a secluded stretch of beach, accompanied by the sounds of the sea.',
  duration: '45 Min',
  category: 'Wellness'
}, {
  title: "Chef's Breakfast Experience",
  description: 'Watch a private chef prepare a bespoke breakfast using locally sourced ingredients, paired with artisanal coffee.',
  duration: '1.5 Hours',
  category: 'Gastronomy'
}, {
  title: 'Morning Coastal Hike',
  description: 'Explore hidden trails along dramatic cliffs with a naturalist guide, discovering local flora and breathtaking viewpoints.',
  duration: '2 Hours',
  category: 'Adventure'
}, {
  title: 'Resort Spa Morning Ritual',
  description: 'An indulgent morning treatment combining aromatherapy, hot stones, and indigenous healing techniques.',
  duration: '2 Hours',
  category: 'Wellness'
}, {
  title: 'Guided Nature Walk',
  description: 'Discover the local ecosystem with an expert botanist, learning about native plants and wildlife.',
  duration: '1.5 Hours',
  category: 'Nature'
}, {
  title: 'Poolside Wellness Session',
  description: 'Aquatic stretching and relaxation exercises in the resort infinity pool, followed by fresh pressed juices.',
  duration: '1 Hour',
  category: 'Wellness'
}, {
  title: 'Sunrise Photography Walk',
  description: 'Capture the golden hour with a professional photographer who knows the most stunning vantage points.',
  duration: '1.5 Hours',
  category: 'Culture'
}, {
  title: 'Morning Market Visit',
  description: 'Explore the vibrant local market with your concierge, selecting fresh ingredients for a private cooking session later.',
  duration: '2 Hours',
  category: 'Culture'
}, {
  title: 'Tennis & Smoothie Bar',
  description: 'A private tennis lesson on the resort courts followed by bespoke smoothies at the wellness bar.',
  duration: '1.5 Hours',
  category: 'Sport'
}];
const afternoonPool = [{
  title: 'Private Sunset Yacht Cruise',
  description: 'Sail along the coastline aboard a luxury yacht with champagne, canapés, and a dedicated crew.',
  duration: '3 Hours',
  category: 'Exclusive'
}, {
  title: 'Guided Historical & Cultural Tour',
  description: 'A private walking tour through centuries of history, with exclusive access to landmark sites and hidden courtyards.',
  duration: '4 Hours',
  category: 'Culture'
}, {
  title: 'Exclusive Wine & Culinary Tasting',
  description: 'A curated gastronomic journey featuring local vintages paired with a multi-course tasting menu by a Michelin-starred chef.',
  duration: '2.5 Hours',
  category: 'Gastronomy'
}, {
  title: 'Snorkeling at Hidden Coves',
  description: 'A private boat takes you to crystal-clear coves for an unforgettable snorkeling experience with marine life.',
  duration: '3 Hours',
  category: 'Adventure'
}, {
  title: 'Local Artisan Workshop',
  description: 'Learn traditional craftsmanship from a master artisan — pottery, weaving, or glassblowing depending on the region.',
  duration: '2 Hours',
  category: 'Culture'
}, {
  title: 'Horseback Riding Along the Coast',
  description: 'Ride through scenic trails and along sandy beaches on well-trained horses, guided by an experienced equestrian.',
  duration: '2 Hours',
  category: 'Adventure'
}, {
  title: 'Cooking Class with Local Chef',
  description: 'Master regional dishes in a hands-on class, then enjoy your creations paired with local wines.',
  duration: '3 Hours',
  category: 'Gastronomy'
}, {
  title: 'Private Island Excursion',
  description: 'A speedboat whisks you to a nearby island for a private picnic, swimming, and exploration.',
  duration: 'Half Day',
  category: 'Exclusive'
}, {
  title: 'Luxury Shopping Tour',
  description: 'A curated tour of the finest local boutiques and ateliers with a personal stylist and VIP access.',
  duration: '3 Hours',
  category: 'Lifestyle'
}, {
  title: 'Holistic Spa & Wellness Retreat',
  description: 'An afternoon of rejuvenation with a bespoke wellness package featuring indigenous healing rituals.',
  duration: 'Half Day',
  category: 'Wellness'
}];
const eveningPool = [{
  title: 'Michelin-Star Dining Experience',
  description: "An intimate multi-course dinner at the region's most celebrated restaurant, with sommelier-selected wine pairings.",
  duration: '3 Hours',
  category: 'Gastronomy'
}, {
  title: 'Rooftop Cocktail Masterclass',
  description: 'Learn to craft signature cocktails with a master mixologist while enjoying panoramic sunset views.',
  duration: '1.5 Hours',
  category: 'Lifestyle'
}, {
  title: 'Sunset Champagne on the Terrace',
  description: 'Unwind with vintage champagne and artisanal cheeses as the sun dips below the horizon.',
  duration: '1 Hour',
  category: 'Relaxation'
}, {
  title: 'Private Stargazing Experience',
  description: 'An astronomer guides you through the constellations with a premium telescope, accompanied by warm drinks.',
  duration: '1.5 Hours',
  category: 'Exclusive'
}, {
  title: 'Live Jazz & Fine Dining',
  description: 'An evening of world-class jazz performed live while you enjoy a curated tasting menu.',
  duration: '3 Hours',
  category: 'Entertainment'
}, {
  title: 'Night Market & Street Food Tour',
  description: 'Discover the vibrant local nightlife and authentic street food with a knowledgeable local guide.',
  duration: '2.5 Hours',
  category: 'Culture'
}, {
  title: 'Beachside Bonfire Dinner',
  description: 'A private dinner set up on the beach around a bonfire, with a personal chef and acoustic music.',
  duration: '2.5 Hours',
  category: 'Exclusive'
}, {
  title: 'Wine Cellar Private Tasting',
  description: 'Descend into a historic wine cellar for a rare vintage tasting led by the estate sommelier.',
  duration: '2 Hours',
  category: 'Gastronomy'
}, {
  title: 'Sunset Sailing & Dinner',
  description: 'A twilight sail followed by dinner aboard the yacht, anchored in a secluded bay.',
  duration: '3 Hours',
  category: 'Exclusive'
}, {
  title: 'Cultural Performance & Dinner',
  description: 'Enjoy traditional music and dance performances at a historic venue, paired with a regional feast.',
  duration: '3 Hours',
  category: 'Culture'
}];
function generateItinerary(destination: string, resort: string, departureDate: string, returnDate: string) {
  const start = new Date(departureDate);
  const end = new Date(returnDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  let numDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (numDays < 1) numDays = 1;
  if (numDays > 14) numDays = 14;
  // Check for curated destination data
  const curatedDest = findDestination(destination);
  const days: {
    date: Date;
    activities: DayActivity[];
  }[] = [];
  if (curatedDest) {
    // Use real, curated activities for this destination
    const templates = curatedDest.dayTemplates;
    for (let i = 0; i < numDays; i++) {
      const dayDate = new Date(start);
      dayDate.setDate(start.getDate() + i);
      const template = templates[i % templates.length];
      days.push({
        date: dayDate,
        activities: [{
          ...template.activities[0],
          timeSlot: 'morning' as const
        }, {
          ...template.activities[1],
          timeSlot: 'afternoon' as const
        }, {
          ...template.activities[2],
          timeSlot: 'evening' as const
        }]
      });
    }
    // Use curated transfers
    const transfers: TransferProps[] = curatedDest.transfers.map((t, idx) => ({
      id: `t${idx + 1}`,
      ...t
    }));
    return {
      days,
      numDays,
      transfers,
      isCurated: true
    };
  }
  // Fallback: generic pool for unknown destinations
  for (let i = 0; i < numDays; i++) {
    const dayDate = new Date(start);
    dayDate.setDate(start.getDate() + i);
    const morning = morningPool[i % morningPool.length];
    const afternoon = afternoonPool[i % afternoonPool.length];
    const evening = eveningPool[i % eveningPool.length];
    const personalizeDesc = (desc: string) => desc.replace(/the coastline/g, `the ${destination} coastline`).replace(/the resort/g, resort).replace(/the region/g, destination);
    days.push({
      date: dayDate,
      activities: [{
        ...morning,
        timeSlot: 'morning' as const,
        description: personalizeDesc(morning.description)
      }, {
        ...afternoon,
        timeSlot: 'afternoon' as const,
        description: personalizeDesc(afternoon.description)
      }, {
        ...evening,
        timeSlot: 'evening' as const,
        description: personalizeDesc(evening.description)
      }]
    });
  }
  return {
    days,
    numDays,
    transfers: null,
    isCurated: false
  };
}
export function RecommendationsPanel({
  destination,
  resort,
  departureDate,
  returnDate,
  onReset
}: RecommendationsPanelProps) {
  const result = generateItinerary(destination, resort, departureDate, returnDate);
  const {
    days,
    numDays
  } = result;
  const defaultTransfers: TransferProps[] = [{
    id: 't1',
    type: 'Private Car',
    destination: 'Neighboring Historic District',
    duration: '45 min',
    description: 'Chauffeur-driven Mercedes S-Class with complimentary refreshments.'
  }, {
    id: 't2',
    type: 'Luxury Bus',
    destination: 'Regional Capital City',
    duration: '1.5 hrs',
    description: 'First-class seating, panoramic windows, and onboard concierge.'
  }, {
    id: 't3',
    type: 'Helicopter',
    destination: 'Coastal Viewpoint',
    duration: '20 min',
    description: 'Scenic aerial transfer providing unparalleled views of the landscape.'
  }];
  const transfers: TransferProps[] = result.transfers || defaultTransfers;
  return <motion.div variants={containerVariants} initial="hidden" animate="visible" className="w-full max-w-5xl mx-auto py-12 px-4 sm:px-6 lg:px-8" data-id="element-51">
      {/* Header */}
      <motion.div variants={itemVariants} className="text-center mb-16" data-id="element-52">
        <div className="flex items-center justify-center gap-3 mb-4" data-id="element-53">
          <div className="w-10 h-px bg-gold-400/40" data-id="element-54" />
          <SparklesIcon className="w-4 h-4 text-gold-400" data-id="element-55" />
          <div className="w-10 h-px bg-gold-400/40" data-id="element-56" />
        </div>
        <h2 className="text-3xl md:text-4xl font-serif text-navy-900 mb-4" data-id="element-57">
          Your Curated Itinerary for{' '}
          <span className="gold-gradient-text" data-id="element-58">{destination}</span>
        </h2>
        <p className="text-lg text-navy-800/70 max-w-2xl mx-auto" data-id="element-59">
          {numDays} {numDays === 1 ? 'night' : 'nights'} at {resort},{' '}
          {destination} — every moment thoughtfully planned.
        </p>
        <div className="w-24 h-px bg-gold-400 mx-auto mt-8" data-id="element-60" />
      </motion.div>

      {/* Day-by-Day Itinerary */}
      <section className="mb-20" data-id="element-61">
        <motion.div variants={itemVariants} className="flex items-center space-x-3 mb-10" data-id="element-62">
          <CalendarDaysIcon className="w-6 h-6 text-gold-500" data-id="element-63" />
          <h3 className="text-2xl font-serif text-navy-900" data-id="element-64">
            Day-by-Day Itinerary
          </h3>
        </motion.div>

        <div className="space-y-8" data-id="element-65">
          {days.map((day, index) => <motion.div key={index} variants={itemVariants} data-id="element-66">
              <ItineraryDay dayNumber={index + 1} date={day.date} activities={day.activities} data-id="element-67" />
            </motion.div>)}
        </div>
      </section>

      {/* Transfers Section */}
      <section className="mb-16" data-id="element-68">
        <motion.div variants={itemVariants} className="flex items-center space-x-3 mb-8" data-id="element-69">
          <MapIcon className="w-6 h-6 text-gold-500" data-id="element-70" />
          <h3 className="text-2xl font-serif text-navy-900" data-id="element-71">
            Seamless Transfers
          </h3>
        </motion.div>
        <p className="text-navy-800/50 text-sm mb-6" data-id="element-72">
          Available throughout your stay — arrange with your concierge at any
          time.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-73">
          {transfers.map(transfer => <motion.div key={transfer.id} variants={itemVariants} data-id="element-74">
              <TransferCard transfer={transfer} data-id="element-75" />
            </motion.div>)}
        </div>
      </section>

      {/* Print Section */}
      <PrintSection data-id="element-76" />

      {/* Reset */}
      <motion.div variants={itemVariants} className="mt-16 text-center" data-id="element-77">
        <button onClick={onReset} className="inline-flex items-center space-x-2 text-navy-900 font-medium hover:text-gold-500 transition-colors duration-300 group" data-id="element-78">
          <span data-id="element-79">Plan Another Journey</span>
          <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" data-id="element-80" />
        </button>
      </motion.div>
    </motion.div>;
}
function PrintSection() {
  const [reservationNumber, setReservationNumber] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const handleConfirm = () => {
    if (reservationNumber.trim().length > 0) {
      setConfirmed(true);
    }
  };
  const handlePrint = () => {
    window.print();
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };
  return <motion.section variants={itemVariants} className="mt-16 max-w-2xl mx-auto print:hidden" data-id="element-81">
      <div className="bg-gradient-to-br from-navy-900 via-[#0d1f3c] to-navy-900 rounded border border-gold-500/15 p-8 text-center relative overflow-hidden" data-id="element-82">
        {/* Corner ornaments */}
        <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-gold-400/40 rounded-tl" data-id="element-83" />
        <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-gold-400/40 rounded-tr" data-id="element-84" />
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-gold-400/40 rounded-bl" data-id="element-85" />
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-gold-400/40 rounded-br" data-id="element-86" />

        <PrinterIcon className="w-6 h-6 text-gold-400 mx-auto mb-4" data-id="element-87" />
        <h3 className="text-xl font-serif text-white mb-2" data-id="element-88">
          Print Your Itinerary
        </h3>
        <p className="text-cream-100/50 text-sm mb-6" data-id="element-89">
          Enter your reservation number to print a copy of your curated
          itinerary.
        </p>

        {!confirmed ? <div className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto" data-id="element-90">
            <div className="relative flex-grow w-full" data-id="element-91">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-92">
                <HashIcon className="h-4 w-4 text-gold-400/50" data-id="element-93" />
              </div>
              <input type="text" value={reservationNumber} onChange={e => setReservationNumber(e.target.value)} onKeyDown={handleKeyDown} placeholder="e.g., SLE-2026-48291" className="block w-full pl-9 pr-4 py-3 border border-gold-500/20 rounded bg-black/40 text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-gold-400 focus:border-gold-400 transition-all text-sm" aria-label="Reservation Number" data-id="element-94" />
            </div>
            <button onClick={handleConfirm} disabled={!reservationNumber.trim()} className="w-full sm:w-auto px-6 py-3 rounded bg-gold-500/20 border border-gold-500/30 text-gold-400 text-sm font-medium tracking-wide hover:bg-gold-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0" data-id="element-95">
              Confirm
            </button>
          </div> : <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3
      }} className="space-y-4" data-id="element-96">
            <p className="text-gold-400/80 text-sm" data-id="element-97">
              Reservation{' '}
              <span className="font-medium text-gold-400" data-id="element-98">
                {reservationNumber}
              </span>{' '}
              confirmed.
            </p>
            <button onClick={handlePrint} className="inline-flex items-center gap-2 px-8 py-3 rounded bg-gradient-to-r from-gold-500 to-gold-400 text-navy-900 text-sm font-medium tracking-widest uppercase hover:from-gold-400 hover:to-gold-500 hover:shadow-[0_0_30px_rgba(201,169,110,0.3)] transition-all" data-id="element-99">
              <PrinterIcon className="w-4 h-4" data-id="element-100" />
              Print Itinerary
            </button>
          </motion.div>}
      </div>
    </motion.section>;
}